import React from 'react';
import { Mail } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton } from '@/components/site/CtaButton';
import { WhatsAppButton } from '@/components/site/WhatsAppButton';
import { AGENCY, CONSULTATION_PATH } from '@/lib/site';
import { useI18n } from '@/lib/i18n';

/** Final conversion block — quiet luxury, unmistakable next step. */
export const CtaBlock = ({
  eyebrow,
  title,
  body,
  testIdPrefix = 'home-final-cta',
}) => {
  const { t } = useI18n();

  return (
    <section
      data-testid={testIdPrefix}
      className="relative overflow-hidden border-y border-[color:var(--rmh-border)]"
      style={{ backgroundColor: 'var(--rmh-canvas-2)' }}
    >
      <span
        aria-hidden="true"
        className="rmh-champagne-wash pointer-events-none absolute inset-0"
      />
      <Container className="relative z-10 py-16 text-center lg:py-24">
        <Reveal>
          <div className="flex items-center justify-center gap-3">
            <span
              className="block h-px w-8"
              style={{ backgroundColor: 'var(--rmh-accent)' }}
              aria-hidden="true"
            />
            <span className="rmh-eyebrow">{eyebrow ?? t.ctaBlock.eyebrow}</span>
            <span
              className="block h-px w-8"
              style={{ backgroundColor: 'var(--rmh-accent)' }}
              aria-hidden="true"
            />
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <h2 className="rmh-display mx-auto mt-7 max-w-3xl text-[2rem] sm:text-[2.6rem] lg:text-[3.4rem]">
            {title ?? t.ctaBlock.title}
          </h2>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="rmh-body mx-auto mt-6 max-w-xl text-[0.95rem] sm:text-base">
            {body ?? t.ctaBlock.body}
          </p>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <CtaButton
              to={CONSULTATION_PATH}
              size="lg"
              variant="primary"
              arrow
              testId={`${testIdPrefix}-book-button`}
              className="w-full sm:w-auto"
            >
              {t.common.bookConsultation}
            </CtaButton>
            <WhatsAppButton
              variant="outline"
              size="lg"
              long
              testId={`${testIdPrefix}-whatsapp-link`}
              className="w-full sm:w-auto"
            />
          </div>
        </Reveal>

        <Reveal delay={0.24}>
          <a
            href={`mailto:${AGENCY.email}`}
            data-testid={`${testIdPrefix}-email-link`}
            className="mt-8 inline-flex items-center gap-2 text-[0.84rem] text-[color:var(--rmh-muted)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
          >
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            {AGENCY.email}
          </a>
        </Reveal>
      </Container>
    </section>
  );
};

export default CtaBlock;
